import { ArcSeg2d } from '../arcSeg2d';
import { LineSeg2d } from '../LineSeg2d';
import { IPoint2d } from '../IPoint2d';
import { Line2d } from '../Line2d';
import { Circle2d } from '../circle2d';
import { IShape2d } from '@/editor/types/shape';
import { thresholdValue } from '../const';
import { normalizeAngle } from '../utils';
import { Ray2d } from '../ray2d';
import { dot } from '../vector';
import { Point2dUtils } from '../Point2dUtils';

// 线与圆相交
// null表示没有交点，[]表示有交点
export function intersectLineAndCircle(line: Line2d, circle: Circle2d): IPoint2d[] | null {
  const { A, B, C } = line.generalForm;
  const { x: cx, y: cy } = circle.center;
  const r = circle.radius;

  // 圆心到直线的有符号距离分子
  const signedNumer = A * cx + B * cy + C;
  const denom = A * A + B * B; // = ||(A,B)||²
  const denomSqrt = Math.sqrt(denom);

  // 圆心到直线的距离
  const dist = Math.abs(signedNumer) / denomSqrt;

  if (dist > r + thresholdValue) return null;

  // 垂足坐标
  const footX = cx - (A * signedNumer) / denom;
  const footY = cy - (B * signedNumer) / denom;

  // 相切：只有一个交点
  if (dist >= r - thresholdValue) {
    return [{ x: footX, y: footY }];
  }

  // 半弦长
  const halfChord = Math.sqrt(r * r - dist * dist);

  // 直线方向单位向量 (B, -A) / ||(A,B)||
  const ux = B / denomSqrt;
  const uy = -A / denomSqrt;

  return [
    { x: footX + halfChord * ux, y: footY + halfChord * uy },
    { x: footX - halfChord * ux, y: footY - halfChord * uy }
  ];
}

// 圆弧与线段相交
// null表示没有交点，[]表示有交点
export function intersectArcSegAndLineSeg(arcSeg: ArcSeg2d, line: LineSeg2d): IPoint2d[] | null {
  // 1. 线段所在直线与圆求候选交点
  const fullLine = new Line2d({ points: { p1: line.start, p2: line.end } });
  const circle = new Circle2d({ center: arcSeg.center, radius: arcSeg.radius });
  const candidates = intersectLineAndCircle(fullLine, circle);

  if (!candidates || candidates.length === 0) return null;

  // 判断角度是否在弧的角度范围内（含两端点）
  const angleInArc = (angle: number): boolean => {
    const sweep = arcSeg.getSweepAngle();
    const diff = arcSeg.clockwise
      ? normalizeAngle(arcSeg.startAngle - angle)
      : normalizeAngle(angle - arcSeg.startAngle);
    return diff <= sweep + thresholdValue;
  };

  // 2. 过滤：同时满足在线段上 且 在圆弧角度范围内
  const results = candidates.filter((pt) => {
    if (!line.pointOnSegment(pt)) return false;
    const angle = normalizeAngle(Math.atan2(pt.y - arcSeg.center.y, pt.x - arcSeg.center.x));
    return angleInArc(angle);
  });

  // 3.如果交点离线断端点在误差值范围内，直接取线断端点
  for (let i = 0; i < results.length; i++) {
    if (Point2dUtils.equals(arcSeg.start, results[i])) {
      results[i] = Point2dUtils.clone(arcSeg.start);
    } else if (Point2dUtils.equals(arcSeg.end, results[i])) {
      results[i] = Point2dUtils.clone(arcSeg.end);
    } else if (Point2dUtils.equals(line.start, results[i])) {
      results[i] = Point2dUtils.clone(line.start);
    } else if (Point2dUtils.equals(line.end, results[i])) {
      results[i] = Point2dUtils.clone(line.end);
    }
  }

  return results.length > 0 ? results : null;
}

// 射线与线段相交
export function intersectRayAndLineSeg(ray: Ray2d, lineSeg: LineSeg2d): IPoint2d | null {
  const x1 = lineSeg.start.x,
    y1 = lineSeg.start.y;
  const x2 = lineSeg.end.x,
    y2 = lineSeg.end.y;
  const x3 = ray.origin.x,
    y3 = ray.origin.y;
  const dx = ray.direction.x,
    dy = ray.direction.y;

  // 参数方程联立：
  //   线段：P = start + ua*(end - start),  ua ∈ [0,1]
  //   射线：P = ray.point + t*direction,   t  ≥ 0
  // 行列式（分母）
  const denom = dx * (y2 - y1) - dy * (x2 - x1);

  // 平行或共线，无单一交点
  if (Math.abs(denom) < thresholdValue) return null;

  const ua = (dx * (y3 - y1) - dy * (x3 - x1)) / denom;
  const t = ((x2 - x1) * (y3 - y1) - (y2 - y1) * (x3 - x1)) / denom;

  // ua ∈ [0,1]：交点在线段内；t ≥ 0：交点在射线正方向上
  if (ua < -thresholdValue || ua > 1 + thresholdValue) return null;
  if (t < -thresholdValue) return null;

  return { x: x1 + ua * (x2 - x1), y: y1 + ua * (y2 - y1) };
}

// 射线与圆相交
export function intersectRayAndCircle(ray: Ray2d, circle: Circle2d, sort: boolean = true): IPoint2d[] | null {
  const p2 = { x: ray.origin.x + ray.direction.x, y: ray.origin.y + ray.direction.y };
  const fullLine = new Line2d({ points: { p1: ray.origin, p2 } });
  const candidates = intersectLineAndCircle(fullLine, circle);
  if (!candidates || candidates.length === 0) return null;

  // 过滤：只保留射线正方向（t >= 0）的点
  const validPoints = candidates
    .map((pt) => ({ distance: dot({ x: pt.x - ray.origin.x, y: pt.y - ray.origin.y }, ray.direction), point: pt }))
    .filter((info) => info.distance >= -thresholdValue);

  if (validPoints.length === 0) return null;
  if (validPoints.length === 1) return validPoints.map((info) => info.point);

  if (sort) validPoints.sort((a, b) => a.distance - b.distance);

  return validPoints.map((info) => info.point);
}

// 射线与圆弧相交，返回沿射线方向最近的交点
export function intersectRayAndArcSeg(ray: Ray2d, arcSeg: ArcSeg2d): IPoint2d[] | null {
  // 1. 射线所在直线与圆求候选交点
  const p2 = { x: ray.origin.x + ray.direction.x, y: ray.origin.y + ray.direction.y };
  const fullLine = new Line2d({ points: { p1: ray.origin, p2 } });
  const circle = new Circle2d({ center: arcSeg.center, radius: arcSeg.radius });
  const candidates = intersectLineAndCircle(fullLine, circle);

  if (!candidates || candidates.length === 0) return null;

  // 判断角度是否在弧的角度范围内（含端点）
  const angleInArc = (angle: number): boolean => {
    const sweep = arcSeg.getSweepAngle();
    const diff = arcSeg.clockwise
      ? normalizeAngle(arcSeg.startAngle - angle)
      : normalizeAngle(angle - arcSeg.startAngle);
    return diff <= sweep + thresholdValue;
  };

  // 2. 过滤：在射线正方向（t ≥ 0）且在弧角度范围内
  const valid = candidates
    .map((pt) => {
      const t = (pt.x - ray.origin.x) * ray.direction.x + (pt.y - ray.origin.y) * ray.direction.y;
      const angle = normalizeAngle(Math.atan2(pt.y - arcSeg.center.y, pt.x - arcSeg.center.x));
      return { pt, t, onArc: angleInArc(angle) };
    })
    .filter(({ t, onArc }) => t >= -thresholdValue && onArc)
    .sort((a, b) => a.t - b.t);

  return valid.length > 0 ? valid.map(({ pt }) => pt) : null;
}

// 圆与线段相交
export function intersectCircleAndLineSeg(
  circle: Circle2d,
  lineSeg: LineSeg2d,
  tValue: number = thresholdValue
): IPoint2d[] | null {
  const fullLine = new Line2d({ points: { p1: lineSeg.start, p2: lineSeg.end } });
  const candidates = intersectLineAndCircle(fullLine, circle);
  if (!candidates || candidates.length === 0) return null;

  const result = candidates.filter((pt: IPoint2d) => lineSeg.pointOnSegment(pt, tValue));
  return result.length > 0 ? result : null;
}

// 圆弧与圆相交
export function intersectArcSegAndCircle(arcSeg: ArcSeg2d, circle: Circle2d): IPoint2d[] | null {
  // 1. 两圆求候选交点
  const arcCircle = new Circle2d({ center: arcSeg.center, radius: arcSeg.radius });
  const candidates = arcCircle.intersect(circle);
  if (!candidates || candidates.length === 0) return null;

  // 2. 过滤：候选点必须在弧的角度范围内
  const angleInArc = (angle: number): boolean => {
    const sweep = arcSeg.getSweepAngle();
    const diff = arcSeg.clockwise
      ? normalizeAngle(arcSeg.startAngle - angle)
      : normalizeAngle(angle - arcSeg.startAngle);
    return diff <= sweep + thresholdValue;
  };

  const result = candidates.filter((pt: IPoint2d) => {
    const angle = normalizeAngle(Math.atan2(pt.y - arcSeg.center.y, pt.x - arcSeg.center.x));
    return angleInArc(angle);
  });

  return result.length > 0 ? result : null;
}

// 圆弧与直线相交
export function intersectArcSegAndLine(arcSeg: ArcSeg2d, line: Line2d): IPoint2d[] | null {
  const circle = new Circle2d({ center: arcSeg.center, radius: arcSeg.radius });
  let candidates = intersectLineAndCircle(line, circle);
  if (!candidates) return null;
  candidates = candidates.filter((pt: IPoint2d) => arcSeg.pointOnSegment(pt));

  return candidates.length === 0 ? null : candidates;
}

// null表示没有交点，[]表示有交点
export function intersectCommonShape(
  seg1: IShape2d,
  seg2: IShape2d,
  tValue: number = thresholdValue
): IPoint2d[] | null {
  // 直线与直线相交
  if (seg1 instanceof Line2d && seg2 instanceof Line2d) {
    const point = seg1.intersection(seg2);
    return point ? [point] : null;
  }

  // 线段与线段相交
  if (seg1 instanceof LineSeg2d && seg2 instanceof LineSeg2d) {
    const points = seg1.intersection(seg2);
    return points ? [points] : null;
  }

  // 圆与圆相交
  if (seg1 instanceof Circle2d && seg2 instanceof Circle2d) {
    return seg1.intersect(seg2);
  }

  // 弧与弧相交
  if (seg1 instanceof ArcSeg2d && seg2 instanceof ArcSeg2d) {
    return seg1.intersect(seg2);
  }

  // 线段与射线相交
  if (seg1 instanceof Ray2d && seg2 instanceof LineSeg2d) {
    const point = intersectRayAndLineSeg(seg1, seg2);
    return point ? [point] : null;
  } else if (seg2 instanceof Ray2d && seg1 instanceof LineSeg2d) {
    const point = intersectRayAndLineSeg(seg2, seg1);
    return point ? [point] : null;
  }

  // 圆与线段
  if (seg1 instanceof Circle2d && seg2 instanceof LineSeg2d) {
    return intersectCircleAndLineSeg(seg1, seg2, tValue);
  } else if (seg1 instanceof LineSeg2d && seg2 instanceof Circle2d) {
    return intersectCircleAndLineSeg(seg2, seg1, tValue);
  }

  // 射线与圆弧相交
  if (seg1 instanceof Ray2d && seg2 instanceof ArcSeg2d) {
    const point = intersectRayAndArcSeg(seg1, seg2);
    return point;
  } else if (seg2 instanceof Ray2d && seg1 instanceof ArcSeg2d) {
    const point = intersectRayAndArcSeg(seg2, seg1);
    return point;
  }

  // 线段与圆弧相交
  if (seg1 instanceof LineSeg2d && seg2 instanceof ArcSeg2d) {
    const points = intersectArcSegAndLineSeg(seg2, seg1);
    return points ? points : null;
  } else if (seg2 instanceof LineSeg2d && seg1 instanceof ArcSeg2d) {
    const points = intersectArcSegAndLineSeg(seg1, seg2);
    return points ? points : null;
  }

  // 直线与圆相交
  if (seg1 instanceof Line2d && seg2 instanceof Circle2d) {
    return intersectLineAndCircle(seg1, seg2);
  } else if (seg1 instanceof Circle2d && seg2 instanceof Line2d) {
    return intersectLineAndCircle(seg2, seg1);
  }

  // 圆弧与直线相交
  if (seg1 instanceof ArcSeg2d && seg2 instanceof Line2d) {
    return intersectArcSegAndLine(seg1, seg2);
  } else if (seg1 instanceof Line2d && seg2 instanceof ArcSeg2d) {
    return intersectArcSegAndLine(seg2, seg1);
  }

  if (seg1 instanceof Circle2d && seg2 instanceof ArcSeg2d) {
    return intersectArcSegAndCircle(seg2, seg1);
  } else if (seg1 instanceof ArcSeg2d && seg2 instanceof Circle2d) {
    return intersectArcSegAndCircle(seg1, seg2);
  }

  // 线段与直线相交
  if (seg1 instanceof LineSeg2d && seg2 instanceof Line2d) {
    const point = seg1.intersectionLine(seg2);
    return point ? [point] : null;
  } else if (seg2 instanceof LineSeg2d && seg1 instanceof Line2d) {
    const point = seg2.intersectionLine(seg1);
    return point ? [point] : null;
  }

  // 射线与圆相交
  if (seg1 instanceof Ray2d && seg2 instanceof Circle2d) {
    return intersectRayAndCircle(seg1, seg2);
  } else if (seg2 instanceof Ray2d && seg1 instanceof Circle2d) {
    return intersectRayAndCircle(seg2, seg1);
  }

  return null;
}
